import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import CollectibleCard from "@/components/CollectibleCard";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tag, Sparkles, Trash2, Settings, ArrowLeft } from "lucide-react";

export default function CreatorProfile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isSuperAdmin = user?.role === "super_admin";
  const isOwner = user?.user_id === userId;

  const load = () => {
    Promise.all([api.get(`/creators/${userId}`), api.get("/categories")])
      .then(([pRes, catRes]) => {
        setProfile(pRes.data);
        setCategories(catRes.data);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const sections = useMemo(() => {
    if (!profile) return [];
    const items = profile.collectibles || [];
    const catIds = new Set(categories.map((c) => c.id));
    const result = categories
      .map((cat) => ({ id: cat.id, name: cat.name, items: items.filter((c) => c.category_id === cat.id) }))
      .filter((s) => s.items.length > 0);
    const unc = items.filter((c) => !c.category_id || !catIds.has(c.category_id));
    if (unc.length > 0) result.push({ id: "__uncategorized__", name: "Uncategorized", items: unc });
    return result;
  }, [profile, categories]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/collectibles/${deleteTarget.id}`);
      toast.success("Collectible deleted");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <div className="glow-bg" /><Header />
        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-glow-primary border-t-transparent rounded-full animate-spin shadow-[0_0_20px_rgba(0,240,255,0.4)]" />
        </div>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="relative min-h-screen">
        <div className="glow-bg" /><Header />
        <div className="relative z-10 pt-40 text-center">
          <p className="font-heading text-2xl text-white uppercase tracking-widest">Creator Not Found</p>
          <Link to="/creators" className="font-mono text-glow-primary text-sm mt-4 inline-block">&larr; All Creators</Link>
        </div>
      </div>
    );
  }

  const total = profile.collectibles?.length || 0;

  return (
    <div className="relative min-h-screen">
      <div className="glow-bg" />
      <Header />
      <main className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-28 pb-24">
        <Link to="/creators" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-white/50 hover:text-glow-primary mb-10">
          <ArrowLeft className="w-4 h-4" /> All Creators
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-14 border-b border-white/10 pb-8">
          <div className="flex items-center gap-5">
            {profile.picture ? (
              <img src={profile.picture} alt={profile.name} className="w-20 h-20 rounded-full border border-glow-accent/40 object-cover" data-testid="profile-avatar" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-glow-accent/20 flex items-center justify-center font-heading text-glow-accent text-3xl">
                {(profile.name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-glow-accent">Creator Vault</span>
              <h1 className="font-heading font-900 text-3xl md:text-4xl tracking-tighter text-white mt-2" data-testid="profile-name">
                {profile.name || "Creator"}
              </h1>
              <p className="font-mono text-xs text-white/50 mt-1">{total} {total === 1 ? "collectible" : "collectibles"}</p>
            </div>
          </div>
          <div className="flex gap-3">
            {isOwner && (
              <Link to="/studio">
                <Button className="bg-glow-primary text-black font-bold hover:bg-glow-primary/80 rounded-none font-mono uppercase tracking-wider h-11" data-testid="manage-in-studio-btn">
                  <Settings className="w-4 h-4 mr-2" /> Manage in Studio
                </Button>
              </Link>
            )}
            {isSuperAdmin && !isOwner && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-glow-secondary border border-glow-secondary/40 px-3 py-2 self-center" data-testid="superadmin-mod-badge">
                Super Admin — delete only
              </span>
            )}
          </div>
        </div>

        {total === 0 ? (
          <div className="border border-dashed border-white/15 py-32 text-center" data-testid="profile-empty">
            <p className="font-heading font-500 text-xl text-white/70 uppercase tracking-widest">Empty Vault</p>
          </div>
        ) : (
          <div className="space-y-20" data-testid="profile-sections">
            {sections.map((section) => (
              <section key={section.id} data-testid={`profile-section-${section.id}`}>
                <div className="flex items-center justify-between mb-10 border-b border-white/10 pb-4">
                  <h2 className="font-heading font-500 text-lg uppercase tracking-widest text-white flex items-center gap-3">
                    {section.id === "__uncategorized__" ? <Sparkles className="w-5 h-5 text-glow-primary" /> : <Tag className="w-5 h-5 text-glow-accent" />}
                    {section.name}
                  </h2>
                  <span className="font-mono text-xs text-white/40">{section.items.length} {section.items.length === 1 ? "ITEM" : "ITEMS"}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-[280px] gap-6 md:gap-8">
                  {section.items.map((c, i) => (
                    <div key={c.id} className="relative">
                      <CollectibleCard collectible={c} index={i} />
                      {isSuperAdmin && (
                        <button
                          onClick={() => setDeleteTarget(c)}
                          className="absolute top-3 right-3 z-10 bg-glow-secondary text-white p-2 hover:bg-glow-secondary/80"
                          aria-label="Delete collectible"
                          data-testid={`profile-delete-${c.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-surface border border-white/15 rounded-none" data-testid="profile-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-tight text-white">Delete Collectible?</AlertDialogTitle>
            <AlertDialogDescription className="font-mono text-sm text-white/50">
              As Super Admin you can remove "{deleteTarget?.title}" from this creator's vault. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 text-white/70 hover:bg-white/5 rounded-none font-mono uppercase tracking-wider">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-glow-secondary text-white hover:bg-glow-secondary/80 rounded-none font-mono uppercase tracking-wider"
              data-testid="profile-delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
